import { create } from 'zustand';
import convex from '../convexClient';
import { api } from '../../convex/_generated/api';
import { AUTHOR_BOTS, DEFAULT_AUTHOR_ID } from '../constants/authorBots';
import { DEFAULT_MODEL_TIER } from '../constants/aiTiers';
import { getErrorMessage } from '../utils/errors';

let requestCounter = 0;
const LOCAL_CHAT_STORAGE_PREFIX = 'share-pen:local-chats:';
const isCustomPersonaId = (personaId) => typeof personaId === 'string' && personaId.startsWith('custom:');

const getChatById = (chats, chatId) => chats.find((chat) => chat._id === chatId) || null;
const normalizePersona = (personaId) => ((AUTHOR_BOTS[personaId] || isCustomPersonaId(personaId)) ? personaId : DEFAULT_AUTHOR_ID);
const canUseStorage = () =>
  typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
const getLocalChatStorageKey = (projectId) => `${LOCAL_CHAT_STORAGE_PREFIX}${projectId}`;

const readLocalChatState = (projectId) => {
  if (!canUseStorage()) return null;
  try {
    const raw = window.localStorage.getItem(getLocalChatStorageKey(projectId));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_error) {
    return null;
  }
};

const writeLocalChatState = (projectId, state) => {
  if (!canUseStorage()) return;
  try {
    window.localStorage.setItem(getLocalChatStorageKey(projectId), JSON.stringify(state));
  } catch (_error) {
    // Ignore storage write failures.
  }
};

const makeLocalChat = (projectId, overrides = {}) => {
  const now = Date.now();
  return {
    _id: `local-${projectId}-${now}-${Math.random().toString(36).slice(2, 8)}`,
    projectId,
    title: 'New Chat',
    selectedPersonaId: DEFAULT_AUTHOR_ID,
    selectedTier: DEFAULT_MODEL_TIER,
    allowAgentEdits: false,
    createdAt: now,
    updatedAt: now,
    lastMessageAt: now,
    ...overrides,
  };
};

const makeLocalState = (projectId, seedMessages = []) => {
  const chat = makeLocalChat(projectId);
  return {
    chats: [chat],
    activeChatId: chat._id,
    messagesByChatId: {
      [chat._id]: seedMessages,
    },
  };
};

const cloneLocalMessage = (message) => ({
  ...message,
  attachments: (message.attachments || []).map((attachment) => ({ ...attachment })),
});

const buildLegacyAttachmentText = (attachments = []) =>
  attachments
    .map((attachment) => {
      if (attachment.kind === 'pdf' && attachment.extractedText) {
        return `[Attached PDF: ${attachment.name}]\n${attachment.extractedText}`;
      }
      if (attachment.kind === 'image') {
        return `[Attached image: ${attachment.name}]`;
      }
      return '';
    })
    .filter(Boolean)
    .join('\n\n');

const buildLegacyMessage = (message) => {
  const attachmentText = buildLegacyAttachmentText(message.attachments || []);
  return {
    role: message.role,
    content: attachmentText ? `${message.content}\n\n${attachmentText}`.trim() : message.content,
  };
};

const buildTierRetryHint = (message, selectedTier, afterReadingContext = false) => {
  if (selectedTier !== 'standard') return null;
  if (!/provider returned error/i.test(String(message || ''))) return null;
  if (afterReadingContext) {
    return 'The model provider failed after Share-Pen read your project context. Try again; if it repeats, switch to Extra Hard Thinking.';
  }
  return 'The model provider failed. Try again; if it repeats, switch to Extra Hard Thinking.';
};

const useAIStore = create((set, get) => ({
  backendMode: 'threaded',
  currentProjectId: null,
  chats: [],
  activeChatId: null,
  messages: [],
  messagesByChatId: {},
  isLoading: false,
  isStreaming: false,
  streamingMessage: '',
  error: null,
  selectedPersona: DEFAULT_AUTHOR_ID,
  selectedTier: DEFAULT_MODEL_TIER,
  allowAgentEdits: false,
  thinkingPhase: null,
  thinkingStartedAt: null,

  persistLocalState: () => {
    const projectId = get().currentProjectId;
    if (!projectId) return;
    writeLocalChatState(projectId, {
      chats: get().chats,
      activeChatId: get().activeChatId,
      messagesByChatId: get().messagesByChatId || {},
    });
  },
  appendMessage: (message) =>
    set((state) => ({
      messages: [...state.messages, message],
    })),

  initializeLocalChats: async (projectId) => {
    let localState = readLocalChatState(projectId);
    if (!localState) {
      let seededMessages = [];
      try {
        const legacyMessages = await convex.query(api.ai.listMessages, { projectId });
        seededMessages = (legacyMessages || []).map((msg) => ({
          ...msg,
          attachments: [],
          timestamp: new Date(msg.createdAt || Date.now()).toISOString(),
        }));
      } catch (_error) {
        seededMessages = [];
      }
      localState = makeLocalState(projectId, seededMessages);
      writeLocalChatState(projectId, localState);
    }

    const activeChatId =
      localState.activeChatId && getChatById(localState.chats || [], localState.activeChatId)
        ? localState.activeChatId
        : localState.chats?.[0]?._id || null;
    const activeChat = getChatById(localState.chats || [], activeChatId);

    set({
      backendMode: 'local',
      currentProjectId: projectId,
      chats: localState.chats || [],
      activeChatId,
      messagesByChatId: localState.messagesByChatId || {},
      messages: (localState.messagesByChatId?.[activeChatId] || []).map(cloneLocalMessage),
      selectedPersona: normalizePersona(activeChat?.selectedPersonaId),
      selectedTier: activeChat?.selectedTier || DEFAULT_MODEL_TIER,
      allowAgentEdits: Boolean(activeChat?.allowAgentEdits),
      error: null,
    });
  },

  bootstrapChats: async (projectId) => {
    try {
      const response = await convex.mutation(api.ai.bootstrapChats, { projectId });
      const chats = response?.chats || [];
      const activeChatId = response?.activeChatId || chats[0]?._id || null;
      const activeChat = getChatById(chats, activeChatId);

      set({
        backendMode: 'threaded',
        currentProjectId: projectId,
        chats,
        activeChatId,
        messagesByChatId: {},
        selectedPersona: normalizePersona(activeChat?.selectedPersonaId),
        selectedTier: activeChat?.selectedTier || DEFAULT_MODEL_TIER,
        allowAgentEdits: Boolean(activeChat?.allowAgentEdits),
        error: null,
      });

      if (activeChatId) {
        await get().loadMessages(activeChatId);
      } else {
        set({ messages: [] });
      }
    } catch (error) {
      await get().initializeLocalChats(projectId);
    }
  },

  loadChats: async (projectId) => {
    if (get().backendMode === 'local') {
      await get().initializeLocalChats(projectId);
      return;
    }
    try {
      const chats = await convex.query(api.ai.listChats, { projectId });
      const activeChatId = get().activeChatId && getChatById(chats, get().activeChatId)
        ? get().activeChatId
        : chats[0]?._id || null;
      const activeChat = getChatById(chats, activeChatId);
      set({
        currentProjectId: projectId,
        chats,
        activeChatId,
        selectedPersona: normalizePersona(activeChat?.selectedPersonaId || get().selectedPersona),
        selectedTier: activeChat?.selectedTier || get().selectedTier,
        allowAgentEdits:
          typeof activeChat?.allowAgentEdits === 'boolean'
            ? activeChat.allowAgentEdits
            : get().allowAgentEdits,
      });
    } catch (error) {
      await get().initializeLocalChats(projectId);
    }
  },

  loadMessages: async (chatId) => {
    if (get().backendMode === 'local') {
      const chat = getChatById(get().chats, chatId);
      set((state) => ({
        activeChatId: chatId,
        messages: (state.messagesByChatId[chatId] || []).map(cloneLocalMessage),
        selectedPersona: normalizePersona(chat?.selectedPersonaId || state.selectedPersona),
        selectedTier: chat?.selectedTier || state.selectedTier,
        allowAgentEdits:
          typeof chat?.allowAgentEdits === 'boolean' ? chat.allowAgentEdits : state.allowAgentEdits,
      }));
      get().persistLocalState();
      return;
    }
    try {
      const messages = await convex.query(api.ai.listMessages, { chatId });
      const chat = getChatById(get().chats, chatId);
      set({
        activeChatId: chatId,
        messages: messages.map((msg) => ({
          ...msg,
          attachments: msg.attachments || [],
          timestamp: new Date(msg.createdAt).toISOString(),
        })),
        selectedPersona: normalizePersona(chat?.selectedPersonaId || get().selectedPersona),
        selectedTier: chat?.selectedTier || get().selectedTier,
        allowAgentEdits:
          typeof chat?.allowAgentEdits === 'boolean' ? chat.allowAgentEdits : get().allowAgentEdits,
      });
    } catch (error) {
      if (get().currentProjectId) {
        await get().initializeLocalChats(get().currentProjectId);
      } else {
        set({ error: getErrorMessage(error, 'Failed to load chat history.') });
      }
    }
  },

  createChat: async (projectId) => {
    if (get().backendMode === 'local') {
      const chat = makeLocalChat(projectId, {
        selectedPersonaId: get().selectedPersona,
        selectedTier: get().selectedTier,
        allowAgentEdits: get().allowAgentEdits,
      });
      set((state) => {
        const chats = [chat, ...state.chats];
        const messagesByChatId = {
          ...state.messagesByChatId,
          [chat._id]: [],
        };
        return {
          currentProjectId: projectId,
          chats,
          activeChatId: chat._id,
          messages: [],
          messagesByChatId,
          selectedPersona: normalizePersona(chat.selectedPersonaId),
          selectedTier: chat.selectedTier,
        };
      });
      get().persistLocalState();
      return chat;
    }
    try {
      const chat = await convex.mutation(api.ai.createChat, { projectId });
      const chats = [chat, ...get().chats];
      set({
        chats,
        activeChatId: chat._id,
        messages: [],
        selectedPersona: normalizePersona(chat.selectedPersonaId),
        selectedTier: chat.selectedTier,
        allowAgentEdits: Boolean(chat.allowAgentEdits),
      });
      return chat;
    } catch (error) {
      set({ error: getErrorMessage(error, 'Failed to create a new chat.') });
      return null;
    }
  },

  renameChat: async (chatId, title) => {
    if (get().backendMode === 'local') {
      let renamedChat = null;
      set((state) => {
        const chats = state.chats.map((item) => {
          if (item._id !== chatId) return item;
          renamedChat = {
            ...item,
            title: title.trim() || item.title,
            updatedAt: Date.now(),
          };
          return renamedChat;
        });
        return { chats };
      });
      get().persistLocalState();
      return renamedChat;
    }
    try {
      const chat = await convex.mutation(api.ai.renameChat, { chatId, title });
      set((state) => ({
        chats: state.chats.map((item) => (item._id === chatId ? chat : item)),
      }));
      return chat;
    } catch (error) {
      set({ error: getErrorMessage(error, 'Failed to rename chat.') });
      return null;
    }
  },

  deleteChat: async (projectId, chatId) => {
    if (get().backendMode === 'local') {
      let nextActiveChatId = null;
      set((state) => {
        const chats = state.chats.filter((item) => item._id !== chatId);
        const messagesByChatId = { ...state.messagesByChatId };
        delete messagesByChatId[chatId];
        if (chats.length === 0) {
          const replacement = makeLocalChat(projectId, {
            selectedPersonaId: state.selectedPersona,
            selectedTier: state.selectedTier,
          });
          chats.push(replacement);
          messagesByChatId[replacement._id] = [];
        }
        nextActiveChatId = chats[0]?._id || null;
        const activeChat = getChatById(chats, nextActiveChatId);
        return {
          chats,
          activeChatId: nextActiveChatId,
          messagesByChatId,
          messages: (messagesByChatId[nextActiveChatId] || []).map(cloneLocalMessage),
          selectedPersona: normalizePersona(activeChat?.selectedPersonaId),
          selectedTier: activeChat?.selectedTier || DEFAULT_MODEL_TIER,
          allowAgentEdits:
            typeof activeChat?.allowAgentEdits === 'boolean'
              ? activeChat.allowAgentEdits
              : state.allowAgentEdits,
        };
      });
      get().persistLocalState();
      return nextActiveChatId;
    }
    try {
      const response = await convex.mutation(api.ai.deleteChat, { chatId });
      await get().loadChats(projectId);
      const activeChatId = response?.activeChatId || get().chats[0]?._id || null;
      if (activeChatId) {
        await get().loadMessages(activeChatId);
      } else {
        set({ messages: [], activeChatId: null });
      }
      return activeChatId;
    } catch (error) {
      set({ error: getErrorMessage(error, 'Failed to delete chat.') });
      return null;
    }
  },

  setSelectedPersona: async (persona) => {
    const nextPersona = (AUTHOR_BOTS[persona] || isCustomPersonaId(persona)) ? persona : get().selectedPersona;
    set({ selectedPersona: nextPersona });
    if (!get().activeChatId) return;
    if (get().backendMode === 'local') {
      set((state) => ({
        chats: state.chats.map((item) =>
          item._id === state.activeChatId
            ? { ...item, selectedPersonaId: nextPersona, updatedAt: Date.now() }
            : item
        ),
      }));
      get().persistLocalState();
      return;
    }
    try {
      const chat = await convex.mutation(api.ai.updateChatPreferences, {
        chatId: get().activeChatId,
        selectedPersonaId: nextPersona,
      });
      set((state) => ({
        chats: state.chats.map((item) => (item._id === chat._id ? chat : item)),
      }));
    } catch (error) {
      set({ error: getErrorMessage(error, 'Failed to update chat persona.') });
    }
  },

  setSelectedTier: async (tier) => {
    set({ selectedTier: tier });
    if (!get().activeChatId) return;
    if (get().backendMode === 'local') {
      set((state) => ({
        chats: state.chats.map((item) =>
          item._id === state.activeChatId
            ? { ...item, selectedTier: tier, updatedAt: Date.now() }
            : item
        ),
      }));
      get().persistLocalState();
      return;
    }
    try {
      const chat = await convex.mutation(api.ai.updateChatPreferences, {
        chatId: get().activeChatId,
        selectedTier: tier,
      });
      set((state) => ({
        chats: state.chats.map((item) => (item._id === chat._id ? chat : item)),
      }));
    } catch (error) {
      set({ error: getErrorMessage(error, 'Failed to update chat tier.') });
    }
  },

  setAllowAgentEdits: async (allowAgentEdits) => {
    const nextValue = Boolean(allowAgentEdits);
    set({ allowAgentEdits: nextValue });
    if (!get().activeChatId) return;
    if (get().backendMode === 'local') {
      set((state) => ({
        chats: state.chats.map((item) =>
          item._id === state.activeChatId
            ? { ...item, allowAgentEdits: nextValue, updatedAt: Date.now() }
            : item
        ),
      }));
      get().persistLocalState();
      return;
    }
    try {
      const chat = await convex.mutation(api.ai.updateChatPreferences, {
        chatId: get().activeChatId,
        allowAgentEdits: nextValue,
      });
      set((state) => ({
        chats: state.chats.map((item) => (item._id === chat._id ? chat : item)),
      }));
    } catch (error) {
      set({ error: getErrorMessage(error, 'Failed to update agent edit mode.') });
    }
  },

  stopGeneration: () => {
    requestCounter += 1;
    set({
      isLoading: false,
      isStreaming: false,
      streamingMessage: '',
      error: null,
      thinkingPhase: null,
      thinkingStartedAt: null,
    });
  },

  sendMessage: async (projectId, content, context = {}) => {
    if (!content?.trim()) return;
    if (get().isLoading || get().isStreaming || !get().activeChatId) return;

    const currentRequest = requestCounter + 1;
    requestCounter = currentRequest;

    set({
      isLoading: true,
      error: null,
      isStreaming: false,
      thinkingPhase: 'planning',
      thinkingStartedAt: Date.now(),
    });

    const attachments = context.attachments || [];
    const userMessage = {
      role: 'user',
      content,
      attachments,
      timestamp: new Date().toISOString(),
    };

    set((state) => ({
      messages: [...state.messages, userMessage],
    }));

    if (get().backendMode === 'local') {
      set((state) => {
        const nextMessages = [...(state.messagesByChatId[state.activeChatId] || []), cloneLocalMessage(userMessage)];
        const nextChats = state.chats.map((chat) =>
          chat._id === state.activeChatId
            ? {
              ...chat,
              title: nextMessages.length === 1
                ? content.trim().slice(0, 60) || chat.title
                : chat.title,
              updatedAt: Date.now(),
              lastMessageAt: Date.now(),
            }
            : chat
        );
        return {
          chats: nextChats,
          messagesByChatId: {
            ...state.messagesByChatId,
            [state.activeChatId]: nextMessages,
          },
        };
      });
      get().persistLocalState();
    } else {
      try {
        await convex.mutation(api.ai.saveMessage, {
          chatId: get().activeChatId,
          role: 'user',
          content,
          attachments: attachments.map((attachment) => ({
            kind: attachment.kind,
            name: attachment.name,
            contentType: attachment.contentType,
            size: attachment.size,
            storageId: attachment.storageId,
            url: attachment.url,
            extractedText: attachment.extractedText,
          })),
        });
        await get().loadChats(projectId);
      } catch (error) {
        console.error('Failed to save user message:', error);
      }
    }

    const history = get().messages
      .filter((msg) => msg.role === 'user' || msg.role === 'assistant')
      .map((msg) => (get().backendMode === 'local'
        ? buildLegacyMessage(msg)
        : {
          role: msg.role,
          content: msg.content,
          attachments: (msg.attachments || []).map((attachment) => ({
            kind: attachment.kind,
            name: attachment.name,
            contentType: attachment.contentType,
            size: attachment.size,
            storageId: attachment.storageId,
            url: attachment.url,
            extractedText: attachment.extractedText,
          })),
        }));

    let assistantMessage = '';

    const { onPendingChanges, attachments: _ignoredAttachments, ...contextPayload } = context;

    if (currentRequest === requestCounter) {
      set({ thinkingPhase: 'reading' });
    }

    try {
      const response = await convex.action(api.ai.sendMessage, {
        projectId,
        chatId: get().backendMode === 'threaded' ? get().activeChatId : undefined,
        messages: history,
        context: contextPayload,
        modelTier: get().selectedTier,
        allowMutatingTools: get().allowAgentEdits,
      });

      if (currentRequest !== requestCounter) return;

      assistantMessage = response?.content || '';

      if (Array.isArray(response?.pendingChanges) && onPendingChanges) {
        await onPendingChanges(response.pendingChanges);
      }

      if (currentRequest !== requestCounter) return;

      if (!assistantMessage) {
        assistantMessage = 'I wasn\'t able to generate a response. Please try again.';
      }

      if (assistantMessage) {
        set({ isStreaming: true, streamingMessage: '', thinkingPhase: 'composing' });
        const chunks = assistantMessage.split(/(\s+)/);
        for (const chunk of chunks) {
          if (currentRequest !== requestCounter) return;
          set((state) => ({
            streamingMessage: state.streamingMessage + chunk,
          }));
          await new Promise((resolve) => setTimeout(resolve, 12));
        }
      }

      if (currentRequest !== requestCounter) return;

      const assistant = {
        role: 'assistant',
        content: assistantMessage,
        personaId: get().selectedPersona,
        attachments: [],
        timestamp: new Date().toISOString(),
      };

      set((state) => ({
        messages: [...state.messages, assistant],
        isLoading: false,
        isStreaming: false,
        streamingMessage: '',
        thinkingPhase: null,
        thinkingStartedAt: null,
      }));

      if (get().backendMode === 'local') {
        set((state) => {
          const nextMessages = [...(state.messagesByChatId[state.activeChatId] || []), cloneLocalMessage(assistant)];
          return {
            messagesByChatId: {
              ...state.messagesByChatId,
              [state.activeChatId]: nextMessages,
            },
          };
        });
        get().persistLocalState();
      } else {
        try {
          await convex.mutation(api.ai.saveMessage, {
            chatId: get().activeChatId,
            role: 'assistant',
            content: assistantMessage,
            personaId: get().selectedPersona,
          });
          await get().loadChats(projectId);
        } catch (error) {
          console.error('Failed to save assistant message:', error);
        }
      }
    } catch (error) {
      if (currentRequest !== requestCounter) return;
      const baseMessage = getErrorMessage(error, 'I ran into an issue. Try again.');
      const message = buildTierRetryHint(baseMessage, get().selectedTier, false) || baseMessage;
      set({
        error: message,
        isLoading: false,
        isStreaming: false,
        streamingMessage: '',
        thinkingPhase: null,
        thinkingStartedAt: null,
      });
      set((state) => ({
        messages: [
          ...state.messages,
          {
            role: 'assistant',
            content: `I ran into an issue: ${message}. Try again.`,
            personaId: get().selectedPersona,
            attachments: [],
            timestamp: new Date().toISOString(),
            isError: true,
          },
        ],
      }));
    }
  },

  clearChat: async () => {
    if (!get().activeChatId) return;
    if (get().backendMode === 'local') {
      set((state) => ({
        messages: [],
        messagesByChatId: {
          ...state.messagesByChatId,
          [state.activeChatId]: [],
        },
        chats: state.chats.map((chat) =>
          chat._id === state.activeChatId
            ? { ...chat, title: 'New Chat', updatedAt: Date.now(), lastMessageAt: chat.createdAt }
            : chat
        ),
      }));
      get().persistLocalState();
      return;
    }
    try {
      await convex.mutation(api.ai.clearMessages, { chatId: get().activeChatId });
      set({ messages: [] });
    } catch (error) {
      set({ error: getErrorMessage(error, 'Failed to clear chat history.') });
    }
  },

  reset: () => {
    set({
      backendMode: 'threaded',
      currentProjectId: null,
      chats: [],
      activeChatId: null,
      messages: [],
      messagesByChatId: {},
      isLoading: false,
      isStreaming: false,
      streamingMessage: '',
      error: null,
      selectedPersona: DEFAULT_AUTHOR_ID,
      selectedTier: DEFAULT_MODEL_TIER,
      allowAgentEdits: false,
      thinkingPhase: null,
      thinkingStartedAt: null,
    });
  },
}));

export default useAIStore;
